import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Link, useNavigate } from 'react-router-dom';
import { ChevronLeft } from 'lucide-react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { api } from '../../utils/api.js';
import { useAuth } from '../../context/AuthContext.jsx';
import { locationHooks } from '../../hooks/useMasters.js';
import Button from '../../components/ui/Button.jsx';
import Input from '../../components/ui/Input.jsx';
import Select from '../../components/ui/Select.jsx';
import Card from '../../components/ui/Card.jsx';

const schema = z.object({
  contentDescription: z.string().min(1, 'Required'),
  quantity: z.string().optional(),
  transitTimeMinutes: z.coerce.number().min(1, 'Required'),
  carrierType: z.enum(['DRIVER','HAND_CARRY']),
  driverName: z.string().optional(),
  vehicleNumber: z.string().optional(),
  driverContact: z.string().optional(),
  receiverId: z.string().optional(),
  receiverText: z.string().optional(),
  remarks: z.string().optional(),
  sourceLocationId: z.string().optional(),
});

export default function DeliveryNoteFormPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { data: locations = [] } = locationHooks.useList();
  const { data: users = [] } = useQuery({ queryKey: ['users-list'], queryFn: () => api.get('/masters/users').then(r => r.data.data) });

  const { register, handleSubmit, watch, formState: { errors, isSubmitting } } = useForm({
    resolver: zodResolver(schema),
    defaultValues: { carrierType: 'DRIVER', sourceLocationId: user?.locations?.[0]?.locationId || '' },
  });

  const createMut = useMutation({
    mutationFn: (data) => api.post('/delivery-notes', data).then(r => r.data.data),
  });

  const onSubmit = async (values) => {
    const dn = await createMut.mutateAsync(values);
    navigate(`/delivery-notes/${dn.id}`);
  };

  const watchCarrier = watch('carrierType');

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6 max-w-2xl mx-auto">
      <div className="flex items-center gap-3">
        <Link to="/delivery-notes" className="text-gray-400 hover:text-gray-600"><ChevronLeft className="w-5 h-5" /></Link>
        <h1 className="text-xl font-bold text-gray-900">New Delivery Note</h1>
      </div>

      <Card title="Contents">
        <div className="p-6 space-y-4">
          <Input label="Description of contents" required error={errors.contentDescription?.message} {...register('contentDescription')} />
          <div className="grid grid-cols-2 gap-4">
            <Input label="Quantity" placeholder="e.g. 3 boxes, 2 kg" {...register('quantity')} />
            <Input label="Transit time (minutes)" type="number" required error={errors.transitTimeMinutes?.message} {...register('transitTimeMinutes')} />
          </div>
          <Input label="Remarks" {...register('remarks')} />
        </div>
      </Card>

      <Card title="Carrier">
        <div className="p-6 space-y-4">
          <Select label="Carrier type" {...register('carrierType')}>
            <option value="DRIVER">Driver / Vehicle</option>
            <option value="HAND_CARRY">Hand Carry</option>
          </Select>
          {watchCarrier === 'DRIVER' && (
            <div className="grid grid-cols-2 gap-4">
              <Input label="Driver name" {...register('driverName')} />
              <Input label="Vehicle number" {...register('vehicleNumber')} />
              <Input label="Driver contact" {...register('driverContact')} />
            </div>
          )}
        </div>
      </Card>

      <Card title="Receiver">
        <div className="p-6 space-y-4">
          <Select label="Receiving staff (internal)" {...register('receiverId')}>
            <option value="">Select user…</option>
            {users.map(u => <option key={u.id} value={u.id}>{u.name} — {u.email}</option>)}
          </Select>
          <Input label="Or enter receiver name (external)" placeholder="External recipient" {...register('receiverText')} />
        </div>
      </Card>

      <Card title="Source Location">
        <div className="p-6">
          <Select label="Sending from" {...register('sourceLocationId')}>
            <option value="">Select location…</option>
            {locations.map(l => <option key={l.id} value={l.id}>{l.code} — {l.name}</option>)}
          </Select>
        </div>
      </Card>

      <div className="flex justify-end gap-3 pb-6">
        <Link to="/delivery-notes"><Button variant="secondary" type="button">Cancel</Button></Link>
        <Button type="submit" loading={isSubmitting}>Create Delivery Note</Button>
      </div>
    </form>
  );
}
